import { MASK_CHARS } from "./constants";

/**
 * This function applies the mask to the input value, returning a new string that matches the mask's format.
 * @param value - a string representing the raw input value (e.g., what the user typed)
 * @param mask -  a string representing the desired input mask (e.g., "99/99/9999" for a date)
 * @param defaultMaskCharacter - a string representing the default mask character (e.g., "_")
 * @returns a string representing the masked value (e.g., "12/31/2025" for a date)
 *
 * @example
 * applyMask("1234567890", "99/99/9999", "_") // "12/31/2025"
 * applyMask("1234567890", "999-999-9999", "-") // "123-456-7890"
 * applyMask("123", "99/99", "_") // "12/3_"
 */
export function applyMask(
  value: string,
  mask: string,
  defaultMaskCharacter: string,
) {
  let maskedValue = "";
  let valueIndex = 0;
  for (let i = 0; i < mask.length; i++) {
    const maskChar = mask[i];
    if (MASK_CHARS.includes(maskChar)) {
      // Find next acceptable char in value
      while (
        valueIndex < value.length &&
        !isAcceptableChar(value[valueIndex], maskChar)
      ) {
        valueIndex++;
      }
      if (valueIndex < value.length) {
        maskedValue += value[valueIndex];
        valueIndex++;
      } else {
        maskedValue += defaultMaskCharacter;
      }
    } else {
      maskedValue += maskChar;
    }
  }
  return maskedValue;
}
/**
 * This function checks if a character is acceptable for a given mask character.
 * @param char - a string representing the character to check
 * @param maskChar - a string representing the mask character
 * @returns a boolean indicating if the character is acceptable
 */
function isAcceptableChar(char: string, maskChar: string) {
  if (maskChar === "9") {
    return /\d/.test(char);
  }
  if (maskChar === "a") {
    return /[a-zA-Z]/.test(char);
  }
  if (maskChar === "*") {
    return /[a-zA-Z0-9]/.test(char);
  }
  return false;
}
/**
 * This function finds the next open character in a given value.
 * @param value - a string representing the value to search
 * @param defaultMaskCharacter - a string representing the default mask character (e.g., "_")
 * @returns a number representing the index of the next open character
 */
export function getNextOpenCharacterIndex(
  value: string,
  defaultMaskCharacter: string,
) {
  let idx = value.indexOf(defaultMaskCharacter);
  if (idx < 0) {
    idx = value.length;
  }
  return idx;
}
/**
 * This function finds the first non-fixed character in the value.
 * @param startingIndex - a number representing the index to start searching from
 * @param fixedCharacterIndices - an array of numbers representing the indices of the fixed characters
 * @returns a number representing the index of the first non-fixed character
 */
export function getFirstNonFixedCharacterIndex(
  startingIndex: number,
  fixedCharacterIndices: number[],
) {
  let idx = startingIndex;
  while (fixedCharacterIndices.includes(idx)) {
    idx++;
  }
  return idx;
}
/**
 * This function finds the last filled character in a given value.
 * @param value - a string representing the value to search
 * @param mask - a string representing the mask
 * @returns a number representing the index of the last filled character
 */
export function getLastFilledCharacterIndex(value: string, mask: string) {
  let idx = value
    .split("")
    .findLastIndex((char, i) => isAcceptableChar(char, mask[i]));
  if (idx < 0) {
    idx = 0;
  }
  return idx;
}
/**
 * This function finds the first filled character in a given value.
 * @param value - a string representing the value to search
 * @param mask - a string representing the mask
 * @returns a number representing the index of the first filled character
 */
export function getFirstFilledCharacterIndex(value: string, mask: string) {
  let idx = value
    .split("")
    .findIndex((char, i) => isAcceptableChar(char, mask[i]));
  if (idx < 0) {
    idx = 0;
  }
  return idx;
}
